import React from 'react';
import { ChevronDown, Lock, HelpCircle, Mail, MessageSquare, Info } from 'lucide-react';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, white, #f3f0ff, #fce7f3)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  nav: {
    position: 'fixed',
    width: '100%',
    zIndex: 50,
    top: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  navContent: {
    maxWidth: '80rem',
    margin: '0 auto',
    padding: '1rem 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  navBrand: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    backgroundImage: 'linear-gradient(to right, #2563eb, #a855f7, #ec4899)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  navLinks: {
    display: 'flex',
    gap: '1.5rem',
  },
  navLink: {
    background: 'none',
    border: 'none',
    color: '#374151',
    fontWeight: '500',
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'color 0.3s',
  },
  hero: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '5rem',
    overflow: 'hidden',
  },
  blob: {
    position: 'absolute',
    borderRadius: '50%',
    mixBlendMode: 'multiply',
    filter: 'blur(40px)',
    opacity: 0.7,
  },
  heroContent: {
    position: 'relative',
    zIndex: 10,
    textAlign: 'center',
    maxWidth: '42rem',
    margin: '0 auto',
    padding: '0 1.5rem',
  },
  heroLogo: {
    height: '8rem',
    margin: '0 auto 3rem',
    filter: 'drop-shadow(0 10px 8px rgba(0, 0, 0, 0.1))',
  },
  heroTagline: {
    fontSize: '1.25rem',
    color: '#4b5563',
    marginBottom: '3rem',
    lineHeight: 1.5,
  },
  button: {
    padding: '1rem 2rem',
    backgroundImage: 'linear-gradient(to right, #2563eb, #a855f7, #ec4899)',
    color: 'white',
    fontWeight: 'bold',
    borderRadius: '9999px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.125rem',
    marginBottom: '4rem',
    transition: 'all 0.3s',
    boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
  },
  section: {
    padding: '5rem 1.5rem',
    maxWidth: '56rem',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '2rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  sectionSubtitle: {
    fontSize: '1.125rem',
    color: '#4b5563',
    marginBottom: '3rem',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '2rem',
    borderRadius: '1rem',
    border: '1px solid rgba(192, 132, 250, 0.3)',
    marginBottom: '1.5rem',
    transition: 'all 0.3s',
  },
  cardTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '1rem',
    marginTop: 0,
  },
  cardText: {
    color: '#4b5563',
    lineHeight: 1.6,
    margin: 0,
  },
  list: {
    listStyleType: 'disc',
    marginLeft: '1.25rem',
    color: '#4b5563',
    lineHeight: 1.6,
  },
  footer: {
    backgroundColor: '#111827',
    color: 'white',
    padding: '3rem 1.5rem',
    textAlign: 'center',
  },
  footerContent: {
    maxWidth: '80rem',
    margin: '0 auto',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  footerLink: {
    color: '#9ca3af',
    textDecoration: 'none',
    transition: 'color 0.3s',
  },
};

export default function RunClubSocialLanding() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={styles.container}>
      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navContent}>
          <div style={styles.navBrand}>Run Club Social</div>

          <div style={styles.navLinks}>
            <button
              onClick={() => scrollToSection('about')}
              style={styles.navLink}
              onMouseEnter={(e) => e.target.style.color = '#9333ea'}
              onMouseLeave={(e) => e.target.style.color = '#374151'}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('sms')}
              style={styles.navLink}
              onMouseEnter={(e) => e.target.style.color = '#9333ea'}
              onMouseLeave={(e) => e.target.style.color = '#374151'}
            >
              SMS Policy
            </button>
            <button
              onClick={() => scrollToSection('support')}
              style={styles.navLink}
              onMouseEnter={(e) => e.target.style.color = '#9333ea'}
              onMouseLeave={(e) => e.target.style.color = '#374151'}
            >
              Support
            </button>
            <button
              onClick={() => scrollToSection('privacy')}
              style={styles.navLink}
              onMouseEnter={(e) => e.target.style.color = '#9333ea'}
              onMouseLeave={(e) => e.target.style.color = '#374151'}
            >
              Privacy
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" style={styles.hero}>
        {/* Animated Blobs */}
        <div style={{ ...styles.blob, top: '5rem', left: '2.5rem', width: '18rem', height: '18rem', background: 'linear-gradient(to bottom right, #c084fc, #f3e8ff)', animation: 'pulse 4s ease-in-out infinite' }}></div>
        <div style={{ ...styles.blob, top: '10rem', right: '2.5rem', width: '18rem', height: '18rem', background: 'linear-gradient(to bottom right, #f472b6, #fce7f3)', animation: 'pulse 4s ease-in-out infinite 2s' }}></div>

        <div style={styles.heroContent}>
          <img
            src="/run_club_social_logo.png"
            alt="Run Club Social"
            style={styles.heroLogo}
          />

          <p style={styles.heroTagline}>
            Connect, run, and grow with your community
          </p>

          <a
            href="https://apps.apple.com/us/app/run-club-social/id6756547154"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = 'https://apps.apple.com/us/app/run-club-social/id6756547154';
            }}
            style={{ ...styles.button, textDecoration: 'none', display: 'inline-block' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 20px 25px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 10px 15px rgba(0, 0, 0, 0.1)';
            }}
          >
            Download App
          </a>

          <div style={{ display: 'flex', justifyContent: 'center', animation: 'bounce 2s infinite' }}>
            <ChevronDown size={32} color="#9333ea" />
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 0.5; }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(10px); }
          }
        `}</style>
      </section>

      {/* About Section */}
      <section id="about" style={{ ...styles.section, background: 'linear-gradient(to right, #f3f0ff, #fce7f3)', maxWidth: '100%', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
          <div style={styles.sectionTitle}>
            <Info size={32} color="#9333ea" />
            About Run Club Social
          </div>
          <p style={styles.sectionSubtitle}>
            Run Club Social is a U.S.-based mobile app designed for running clubs and their members.
            Club organizers can schedule group runs, send event reminders, share routes, and communicate
            with registered participants — all in one place. Available free on iOS.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
            {[
              { title: 'Group Runs', text: 'Organize and join group runs. Set routes, meeting points, pace, and distance.' },
              { title: 'Event Management', text: 'Schedule club events, races, and social runs. Members RSVP and get reminders.' },
              { title: 'Club Communication', text: 'Organizers send updates, cancellations, and announcements to club members.' },
              { title: 'Run Tracking', text: 'Track your runs with GPS, log stats, and share achievements with your club.' },
            ].map((item) => (
              <div key={item.title} style={styles.card}>
                <h3 style={{ ...styles.cardTitle, fontSize: '1.125rem' }}>{item.title}</h3>
                <p style={styles.cardText}>{item.text}</p>
              </div>
            ))}
          </div>
          <p style={{ ...styles.cardText, marginTop: '2rem', fontSize: '0.875rem', color: '#6b7280' }}>
            <strong>Business contact:</strong> <a href="mailto:support@runclubsocialapp.com" style={{ color: '#9333ea' }}>support@runclubsocialapp.com</a>
            &nbsp;·&nbsp; <strong>Platform:</strong> iOS (iPhone)
            &nbsp;·&nbsp; <strong>Country:</strong> United States
          </p>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" style={{ ...styles.section, backgroundColor: 'linear-gradient(to right, #f3f0ff, #fce7f3)' }}>
        <div style={styles.sectionTitle}>
          <HelpCircle size={32} color="#9333ea" />
          Support / Contact Us
        </div>

        <p style={styles.sectionSubtitle}>Need help with Run Club Social? We're here to help.</p>

        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <Mail size={24} color="#9333ea" />
            <h3 style={{ ...styles.cardTitle, margin: 0 }}>Email</h3>
          </div>
          <a href="mailto:support@runclubsocialapp.com" style={{ ...styles.cardText, fontSize: '1.125rem', color: '#9333ea', fontWeight: '500', marginBottom: '1.5rem', textDecoration: 'none', cursor: 'pointer', display: 'block' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
            support@runclubsocialapp.com
          </a>

          <p style={{ ...styles.cardText, fontWeight: '600', marginBottom: '1rem' }}>To help us solve it faster, include:</p>
          <ul style={styles.list}>
            <li>Your account email or username</li>
            <li>iPhone model + iOS version</li>
            <li>What happened and what you expected</li>
            <li>Steps to reproduce</li>
            <li>Screenshots / screen recording (if possible)</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Account Deletion</h3>
          <p style={styles.cardText}>
            You can delete your account inside the app: <span style={{ fontWeight: '600' }}>Menu → Delete Account</span>
          </p>
          <p style={styles.cardText}>
            This permanently deletes your account and associated data (see our Privacy Policy for details). If you have trouble, email <a href="mailto:support@runclubsocialapp.com" style={{ color: '#9333ea', textDecoration: 'none', fontWeight: '600', cursor: 'pointer' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>support@runclubsocialapp.com</a>
          </p>
        </div>
      </section>

      {/* Privacy Policy Section */}
      <section id="privacy" style={styles.section}>
        <div style={styles.sectionTitle}>
          <Lock size={32} color="#ec4899" />
          Privacy Policy
        </div>

        <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '0.875rem' }}>Last Updated: January 27, 2026</p>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>1. Overview</h3>
          <p style={styles.cardText}>
            Run Club Social ("we", "us") respects your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices you have when you use the Run Club Social mobile application.
          </p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>2. Information We Collect</h3>

          <h4 style={{ fontWeight: '600', color: '#1f2937', marginTop: '1rem', marginBottom: '0.5rem' }}>A. Information you provide</h4>
          <ul style={styles.list}>
            <li>Account information: name, email, password (if you sign up with email/password)</li>
            <li>Profile information: username, profile photo, bio, home base (city/state), running preferences (if provided)</li>
            <li>Content you create: posts, comments, and any media you upload (photos/videos)</li>
            <li>Support communications: information you send us when you contact support</li>
          </ul>

          <h4 style={{ fontWeight: '600', color: '#1f2937', marginTop: '1rem', marginBottom: '0.5rem' }}>B. Location and run information</h4>
          <p style={styles.cardText}>Location data when you use features that require it (such as tracking a run or location-based discovery): GPS route/points, distance, pace, time, and related run metrics. We may use Google Maps/Places to power location search and map features.</p>
          <p style={styles.cardText}>Background location (if enabled): If you set iOS Location permission to "Always," the app may collect location while the screen is off during an active run to improve tracking accuracy. You can change this anytime in iOS Settings.</p>

          <h4 style={{ fontWeight: '600', color: '#1f2937', marginTop: '1rem', marginBottom: '0.5rem' }}>C. Device and app information</h4>
          <p style={styles.cardText}>Device/app details such as device type, OS version, and diagnostic/performance information used to improve reliability and troubleshoot issues. We do not use Firebase Analytics or Crashlytics.</p>

          <h4 style={{ fontWeight: '600', color: '#1f2937', marginTop: '1rem', marginBottom: '0.5rem' }}>D. Apple Health (if you enable it)</h4>
          <p style={styles.cardText}>If you choose to connect Apple Health, we may read and/or write workout-related data only with your permission. You can revoke access at any time in Apple Health settings.</p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>3. How We Use Your Information</h3>
          <p style={styles.cardText}>We use information to:</p>
          <ul style={styles.list}>
            <li>Create and manage your account</li>
            <li>Provide core features (runs, events, feed, profiles, run buddy features, blocking)</li>
            <li>Track and display run stats and maps</li>
            <li>Improve the app (bug fixes, performance, feature improvements)</li>
            <li>Help keep the service safe (fraud prevention, abuse prevention, security)</li>
            <li>Respond to support requests and send service-related updates</li>
          </ul>
          <p style={{ ...styles.cardText, fontWeight: '600', marginTop: '1rem' }}>We do not sell your personal information.</p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>4. How We Share Information</h3>

          <h4 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>A. With other users (social features)</h4>
          <p style={styles.cardText}>Other users may be able to see information you choose to share through the app, such as your profile information and posts/comments, and activity related to events or runs you participate in (as shown by the app).</p>

          <h4 style={{ fontWeight: '600', color: '#1f2937', marginTop: '1rem', marginBottom: '0.5rem' }}>B. With service providers</h4>
          <p style={styles.cardText}>We use Firebase (Google) for authentication, database storage, and file storage. These providers process data on our behalf to deliver app functionality.</p>

          <h4 style={{ fontWeight: '600', color: '#1f2937', marginTop: '1rem', marginBottom: '0.5rem' }}>C. For legal and safety reasons</h4>
          <p style={styles.cardText}>We may disclose information if required by law, or to protect users, prevent fraud, and enforce our policies.</p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>5. Data Retention</h3>
          <p style={styles.cardText}>
            We retain information as long as needed to provide the service and operate the app, unless a longer retention period is required by law or needed for legitimate purposes (such as security and fraud prevention).
          </p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>6. Account Deletion</h3>
          <p style={styles.cardText}>
            Delete Account permanently removes your account and associated data, subject to limited retention in backups that are overwritten on a regular schedule.
          </p>
          <p style={styles.cardText}>
            If you need help deleting your account, contact <span style={{ fontWeight: '600' }}>support@runclubsocialapp.com</span>.
          </p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>7. Your Choices</h3>
          <p style={styles.cardText}>You can control permissions at any time:</p>
          <ul style={styles.list}>
            <li>Location: iOS Settings → Privacy & Security → Location Services → Run Club Social</li>
            <li>Apple Health: Apple Health → your profile → Apps → Run Club Social</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>8. Security</h3>
          <p style={styles.cardText}>
            We use reasonable administrative, technical, and physical safeguards designed to protect your information. No system is 100% secure, but we work to protect your data and limit access.
          </p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>9. Children's Privacy</h3>
          <p style={styles.cardText}>
            Run Club Social is not intended for children under 13 (or the minimum legal age required in your country). We do not knowingly collect personal information from children.
          </p>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>10. Changes to This Policy</h3>
          <p style={styles.cardText}>
            We may update this Privacy Policy from time to time. We will update the "Last Updated" date above. Continued use of the app after changes means you accept the updated policy.
          </p>
        </div>

        <div style={{ ...styles.card, backgroundImage: 'linear-gradient(to right, #faf5ff, #fce7f3)', borderColor: 'rgba(147, 51, 234, 0.4)', borderLeft: '4px solid #9333ea' }}>
          <h3 style={styles.cardTitle}>11. SMS / Text Messaging</h3>
          <p style={styles.cardText}>
            If you opt in, Run Club Social may send you text messages about events, group runs, reminders, and account updates. You opt in by providing your mobile number in the app and agreeing to receive texts. <strong>Opt-in is not required to use the app.</strong>
          </p>
          <p style={{ ...styles.cardText, marginTop: '0.75rem' }}>
            <strong>Message frequency varies</strong> based on your club's activity (typically 1–5 messages per week per active club). <strong>Message and data rates may apply.</strong>
          </p>
          <ul style={{ ...styles.list, marginTop: '0.75rem' }}>
            <li>Reply <strong>STOP</strong> to any message to opt out immediately</li>
            <li>Reply <strong>HELP</strong> for assistance, or email <a href="mailto:support@runclubsocialapp.com" style={{ color: '#9333ea' }}>support@runclubsocialapp.com</a></li>
          </ul>
          <p style={{ ...styles.cardText, marginTop: '0.75rem', fontWeight: '600' }}>
            We do not sell, rent, or share your mobile phone number or SMS opt-in consent with any third parties or affiliates for marketing or promotional purposes.
          </p>
          <p style={{ ...styles.cardText, marginTop: '0.75rem' }}>
            For full details, see our <a href="/sms.html" style={{ color: '#9333ea', fontWeight: '600' }}>SMS Messaging &amp; Opt-In Disclosures</a> page.
          </p>
        </div>

        <div style={{ ...styles.card, backgroundImage: 'linear-gradient(to right, #fce7f3, #f3e8ff)', borderColor: 'rgba(236, 72, 153, 0.3)' }}>
          <h3 style={styles.cardTitle}>12. Contact Us</h3>
          <p style={styles.cardText}>
            Questions about privacy or your data? Email: <a href="mailto:support@runclubsocialapp.com" style={{ color: '#9333ea', textDecoration: 'none', fontWeight: '600', cursor: 'pointer' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>support@runclubsocialapp.com</a>
          </p>
        </div>
      </section>

      {/* SMS Policy Section */}
      <section id="sms" style={{ ...styles.section, background: '#faf5ff', maxWidth: '100%', padding: '5rem 1.5rem' }}>
        <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
          <div style={styles.sectionTitle}>
            <MessageSquare size={32} color="#9333ea" />
            SMS Messaging Program
          </div>
          <p style={styles.sectionSubtitle}>
            Run Club Social sends text messages only to members who have explicitly opted in through the mobile app.
          </p>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>What Messages We Send</h3>
            <p style={{ ...styles.cardText, marginBottom: '0.75rem' }}>Messages are strictly transactional and operational:</p>
            <ul style={styles.list}>
              <li>Group-run event reminders and schedule updates</li>
              <li>Cancellation or rescheduling alerts</li>
              <li>Account notifications (registration confirmations, RSVP confirmations)</li>
              <li>Replies from club organizers in response to member messages</li>
            </ul>
            <p style={{ ...styles.cardText, marginTop: '0.75rem', fontWeight: '600' }}>
              We do not send promotional marketing messages.
            </p>
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>How to Opt In</h3>
            <p style={styles.cardText}>
              During account registration in the Run Club Social app, members are presented with a clearly labeled
              opt-in prompt. Members must affirmatively check a box (unchecked by default) to enroll.
              <strong> Opting in to SMS is not required to use the app or any of its features.</strong>
            </p>
            <div style={{ background: '#f3f0ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '1rem', marginTop: '1rem', fontStyle: 'italic', color: '#4b5563' }}>
              "I agree to receive SMS notifications from Run Club Social about my registered events and
              account activity. Message and data rates may apply. Reply STOP to opt out at any time.
              Reply HELP for help."
            </div>
          </div>

          <div style={{ ...styles.card, borderLeft: '4px solid #9333ea', background: 'linear-gradient(to right, #faf5ff, #fff)' }}>
            <h3 style={styles.cardTitle}>Required Disclosures</h3>
            <ul style={styles.list}>
              <li><strong>Program:</strong> Run Club Social — event and account notifications</li>
              <li><strong>Message frequency:</strong> Varies by club activity, typically 1 to 5 messages per week</li>
              <li><strong>Rates:</strong> Message and data rates may apply</li>
              <li><strong>Opt-out:</strong> Reply <strong>STOP</strong> to any message to unsubscribe immediately</li>
              <li><strong>Help:</strong> Reply <strong>HELP</strong> or email <a href="mailto:support@runclubsocialapp.com" style={{ color: '#9333ea' }}>support@runclubsocialapp.com</a></li>
            </ul>
            <p style={{ ...styles.cardText, marginTop: '1rem', fontWeight: '600', color: '#111827' }}>
              We do not sell, rent, share, or disclose your mobile phone number or SMS opt-in data to any
              third parties, affiliates, or lead generators for marketing or promotional purposes.
            </p>
          </div>

          <p style={{ ...styles.cardText, marginTop: '1.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
            For full details see our{' '}
            <a href="/sms.html" style={{ color: '#9333ea', fontWeight: '600' }}>SMS Opt-In Disclosures page</a>,{' '}
            <a href="/privacy.html" style={{ color: '#9333ea' }}>Privacy Policy</a>, and{' '}
            <a href="/terms.html" style={{ color: '#9333ea' }}>Terms and Conditions</a>.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>Run Club Social</p>
          <div style={styles.footerLinks}>
            <a href="#about" style={styles.footerLink} onMouseEnter={(e) => e.target.style.color = '#a78bfa'} onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>About</a>
            <a href="#support" style={styles.footerLink} onMouseEnter={(e) => e.target.style.color = '#a78bfa'} onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Support</a>
            <a href="#sms" style={styles.footerLink} onMouseEnter={(e) => e.target.style.color = '#a78bfa'} onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>SMS Policy</a>
            <a href="/privacy.html" style={styles.footerLink} onMouseEnter={(e) => e.target.style.color = '#a78bfa'} onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Privacy Policy</a>
            <a href="/terms.html" style={styles.footerLink} onMouseEnter={(e) => e.target.style.color = '#a78bfa'} onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>Terms &amp; Conditions</a>
            <a href="mailto:support@runclubsocialapp.com" style={styles.footerLink} onMouseEnter={(e) => e.target.style.color = '#a78bfa'} onMouseLeave={(e) => e.target.style.color = '#9ca3af'}>support@runclubsocialapp.com</a>
          </div>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>&copy; 2026 Run Club Social. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}